import type { AccountWithMetrics } from "../../../api/src/accounts/accounts.service";

/**
 * The two keys read below. `AccountWithMetrics`, `AccountWORiskAllocation` and
 * `AccountWithHoldingsAndRiskAllocation` all carry both.
 */
type AccountHoldingsAndRestrictions = Pick<
  AccountWithMetrics,
  "holdings" | "securityRestrictions"
>;

/**
 * Calculates the percentage of holdings value that are marked as "excluded" securities
 * @param account Single account with holdings and security restrictions
 * @returns Percentage (0-100) of excluded securities by value
 */
export function calculateExcludedSecuritiesPercentage(
  account: AccountHoldingsAndRestrictions,
): number {
  if (!account?.holdings || account.holdings.length === 0) return 0;

  const excludedRestrictions =
    account.securityRestrictions?.filter(
      (restriction) => restriction.treatment === "exclude",
    ) ?? [];

  if (excludedRestrictions.length === 0) return 0;

  const excludedSymbols = new Set(excludedRestrictions.map((r) => r.symbol));

  const totalValue = account.holdings.reduce(
    (sum, holding) => sum + holding.value,
    0,
  );
  const excludedValue = account.holdings
    .filter((holding) =>
      excludedSymbols.has(holding.security?.identifier ?? ""),
    )
    .reduce((sum, holding) => sum + holding.value, 0);

  return totalValue > 0 ? (excludedValue / totalValue) * 100 : 0;
}

/**
 * Calculates the percentage of holdings value that are marked as "excluded" securities
 * across all accounts in a household
 * @param accounts Array of accounts with holdings and security restrictions
 * @returns Percentage (0-100) of excluded securities by value across all accounts
 */
export function calculateHouseholdExcludedSecuritiesPercentage(
  accounts: AccountHoldingsAndRestrictions[],
): number {
  if (!accounts || accounts.length === 0) return 0;

  let totalValue = 0;
  let excludedValue = 0;

  accounts.forEach((account) => {
    if (!account.holdings || account.holdings.length === 0) return;

    const excludedRestrictions =
      account.securityRestrictions?.filter(
        (restriction) => restriction.treatment === "exclude",
      ) ?? [];

    const accountTotal = account.holdings.reduce(
      (sum, holding) => sum + holding.value,
      0,
    );
    totalValue += accountTotal;

    if (excludedRestrictions.length > 0) {
      const excludedSymbols = new Set(
        excludedRestrictions.map((r) => r.symbol),
      );
      const accountExcluded = account.holdings
        .filter((holding) =>
          excludedSymbols.has(holding.security?.identifier ?? ""),
        )
        .reduce((sum, holding) => sum + holding.value, 0);
      excludedValue += accountExcluded;
    }
  });

  return totalValue > 0 ? (excludedValue / totalValue) * 100 : 0;
}
